import {
  FormControl,
  Input,
  Flex,
  Collapse,
  FormErrorMessage,
} from '@chakra-ui/react';
import { LabelWithTooltip } from './LabelWithTooltip';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { setAppLinkingFeature } from '../../../store/redux/slices/template.slice';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import { useRef } from 'react';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';

const GoogleAppLinks = () => {
  const {
    googleAppPlayStoreUrl,
    googleAppPackageName,
    googleWebUrl,
    googleDisplayText,
  } = useAppSelector((s) => s.template.appLinking);
  const dispatch = useAppDispatch();
  const { validateField, handleFocus, handleBlur } =
    useTemplateFieldValidation('appLinking');
  const errors = useAppSelector((s) => s.templateErrors);
  const googleAppPlayStoreUrlRef = useRef<HTMLDivElement>(null);
  const googleAppPackageNameRef = useRef<HTMLDivElement>(null);
  const googleWebUrlRef = useRef<HTMLDivElement>(null);
  const googleDisplayTextRef = useRef<HTMLDivElement>(null);

  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    prefix: 'appLinking',
    fieldRefMap: {
      googleAppPlayStoreUrl: googleAppPlayStoreUrlRef,
      googleAppPackageName: googleAppPackageNameRef,
      googleWebUrl: googleWebUrlRef,
      googleDisplayText: googleDisplayTextRef,
    },
  });

  const handleChange = (field: string, value: string) => {
    validateField(field, value);
    dispatch(setAppLinkingFeature({ [field]: value }));
  };

  return (
    <Flex direction="column" gap={4}>
      <FieldEditIndicator
        fieldRef={googleAppPlayStoreUrlRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="60px"
      >
        <FormControl
          isDisabled={!!googleAppPackageName || !!googleWebUrl}
          isInvalid={!!errors['appLinking.googleAppPlayStoreUrl']}
        >
          <LabelWithTooltip
            label="Google Play Store URL"
            tooltip="Link to your app on Google Play Store."
          />
          <Input
            value={googleAppPlayStoreUrl}
            placeholder="https://play.google.com/store/apps/details?id=com.example.app"
            onChange={(e) =>
              handleChange('googleAppPlayStoreUrl', e.target.value)
            }
            onFocus={() => handleFocus('appLinking.googleAppPlayStoreUrl')}
            onBlur={handleBlur}
          />
          {errors['appLinking.googleAppPlayStoreUrl'] && (
            <FormErrorMessage
              pl={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {errors['appLinking.googleAppPlayStoreUrl']}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={googleAppPackageNameRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="60px"
      >
        <FormControl
          isDisabled={!!googleAppPlayStoreUrl || !!googleWebUrl}
          isInvalid={!!errors['appLinking.googleAppPackageName']}
        >
          <LabelWithTooltip
            label="Package name"
            tooltip="The unique identifier of your app on the Google Play Store."
          />
          <Input
            value={googleAppPackageName}
            placeholder="com.example.app"
            onChange={(e) =>
              handleChange('googleAppPackageName', e.target.value)
            }
            onFocus={() => handleFocus('appLinking.googleAppPackageName')}
            onBlur={handleBlur}
          />
          {errors['appLinking.googleAppPackageName'] && (
            <FormErrorMessage
              pl={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {errors['appLinking.googleAppPackageName']}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={googleWebUrlRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="60px"
      >
        <FormControl
          isDisabled={!!googleAppPlayStoreUrl || !!googleAppPackageName}
          isInvalid={!!errors['appLinking.googleWebUrl']}
        >
          <LabelWithTooltip
            label="Web app URL"
            tooltip="The URL of your web app."
          />
          <Input
            value={googleWebUrl}
            placeholder="https://example.com"
            onChange={(e) => handleChange('googleWebUrl', e.target.value)}
            onFocus={() => handleFocus('appLinking.googleWebUrl')}
            onBlur={handleBlur}
          />
          {errors['appLinking.googleWebUrl'] && (
            <FormErrorMessage
              pl={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {errors['appLinking.googleWebUrl']}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <Collapse
        in={!!googleAppPlayStoreUrl || !!googleAppPackageName || !!googleWebUrl}
      >
        <FieldEditIndicator
          fieldRef={googleDisplayTextRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="60px"
        >
          <FormControl isInvalid={!!errors['appLinking.googleDisplayText']}>
            <LabelWithTooltip
              label="Display text"
              tooltip="Set custom text to be displayed as link text, default is 'Open Pass'"
            />
            <Input
              value={googleDisplayText}
              placeholder="Open Pass"
              onChange={(e) =>
                handleChange('googleDisplayText', e.target.value)
              }
              onFocus={() => handleFocus('appLinking.googleDisplayText')}
              onBlur={handleBlur}
            />
            {errors['appLinking.googleDisplayText'] && (
              <FormErrorMessage
                pl={2}
                display="flex"
                alignItems="center"
                gap={1.5}
              >
                <CustomErrorIcon />
                {errors['appLinking.googleDisplayText']}
              </FormErrorMessage>
            )}
          </FormControl>
        </FieldEditIndicator>
      </Collapse>
    </Flex>
  );
};

export default GoogleAppLinks;
